// Erzwungener Passwortwechsel.
//
// Wird gezeigt, solange das Konto ein Übergabepasswort trägt — also nach der
// Anlage durch den Inhaber oder nach einem Support-Reset. Bewusst ohne
// Navigation und ohne Abbrechen: das Passwort kennt jemand anders, und ein
// Konto mit fremdem Passwort soll nicht benutzbar sein.
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { Feld, Hinweis, Karte, Knopf } from '../../../gemeinsam/komponenten/Basis';
import { LogoKachel } from '../../firma/komponenten/Logo';
import { useSprache } from '../../../gemeinsam/lib/i18n';

export default function PasswortWechseln() {
  const { t } = useSprache();
  const { benutzer, abmelden, aktualisieren } = useAuth();
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  // Dieselben Regeln wie bei der Registrierung — sonst wäre das erzwungene
  // Passwort schwächer als ein selbst gewähltes.
  const langGenug = neu.length >= 8;
  const hatGross = /[A-Z]/.test(neu);
  const hatKlein = /[a-z]/.test(neu);
  const hatZiffer = /[0-9]/.test(neu);
  const stimmtUeberein = neu.length > 0 && neu === wiederholung;
  const andersAlsAlt = neu.length > 0 && neu !== alt;
  const gueltig = langGenug && hatGross && hatKlein && hatZiffer && stimmtUeberein && andersAlsAlt;

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    if (!gueltig || !benutzer) return;
    setSendet(true);
    setFehler(null);
    try {
      await api(`/users/${benutzer.id}`, {
        methode: 'PUT', daten: { oldPassword: alt, password: neu },
      });
      // Der Zustand kommt vom Server — danach ist die Aufforderung weg.
      await aktualisieren();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Passwort konnte nicht geändert werden.');
    } finally { setSendet(false); }
  }

  const regel = (erfuellt: boolean, text: string) => (
    <li className={`flex items-center gap-2 ${erfuellt
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-slate-500 dark:text-slate-400'}`}>
      <span aria-hidden>{erfuellt ? '✓' : '○'}</span>{text}
    </li>
  );

  return (
    <div
      style={{
        // Oben die Statusleiste, unten die Home-Leiste freihalten; im Browser
        // sind beide Werte 0 und es bleibt bei den 3rem von vorher.
        paddingTop: 'calc(3rem + var(--sicher-oben))',
        paddingBottom: 'calc(3rem + var(--sicher-unten))',
      }}
      className="flex min-h-full items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-4 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <LogoKachel groesse="gross" klasse="mx-auto mb-4 shadow-lg shadow-marke-600/25" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('passwort.festlegen')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('passwort.willkommen')}, {benutzer?.name?.split(' ')[0]}
          </p>
        </div>

        <Karte>
          <form onSubmit={absenden} className="space-y-4 p-6">
            <Hinweis art="warnung">
              {t('passwort.warnung')}
            </Hinweis>

            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

            <Feld label={t('passwort.uebermittelt')} type="password" value={alt} required autoFocus
                  autoComplete="current-password"
                  onChange={e => setAlt(e.target.value)} />
            <Feld label={t('passwort.neu')} type="password" value={neu} required
                  autoComplete="new-password"
                  onChange={e => setNeu(e.target.value)} />
            <Feld label={t('passwort.wiederholen')} type="password" value={wiederholung} required
                  autoComplete="new-password"
                  onChange={e => setWiederholung(e.target.value)} />

            <ul className="space-y-1 text-xs">
              {regel(langGenug, t('passwort.regel8'))}
              {regel(hatGross && hatKlein, t('passwort.regelGross'))}
              {regel(hatZiffer, t('passwort.regelZiffer'))}
              {regel(stimmtUeberein, t('passwort.regelGleich'))}
              {regel(andersAlsAlt, t('passwort.regelAnders'))}
            </ul>

            <Knopf type="submit" variante="primaer" className="w-full" laedt={sendet} disabled={!gueltig}>
              {t('passwort.fortfahren')}
            </Knopf>

            <button type="button" onClick={abmelden}
                    className="w-full text-center text-xs text-slate-500 hover:underline dark:text-slate-400">
              {t('aktion.abmelden')}
            </button>
          </form>
        </Karte>
      </div>
    </div>
  );
}
